/**
 * Work order & service file operations.
 *
 * Extracted verbatim from workOrdersService.ts — functions are invoked with
 * `this` bound to the WorkOrdersService instance (WorkOrdersCtx).
 */
import { HttpError } from '../api';
import { extractItems } from '../../utils/apiHelpers';
// Files API
export async function listWorkOrderFiles(workOrderId) {
    try {
        const response = await this.client.get(`/work-orders/${workOrderId}/files`);
        if (!response.success || !response.data) {
            return this.listFailure(response.error || 'Failed to fetch files');
        }
        const rawFiles = extractItems(response.data, ['files']);
        return rawFiles.map((f) => {
            const filename = f.file_name || (typeof f.key === 'string' ? f.key.split('/').pop() : '') || undefined;
            let comment = '';
            if (f.label) {
                const match = f.label.match(/^\[.*?\]\s*(.*)$/);
                comment = match ? match[1].trim() : '';
            }
            return {
                ...f,
                id: f.key,
                name: f.label,
                filename,
                comment: comment || undefined,
            };
        });
    }
    catch (error) {
        console.error('[WorkOrdersService] List files error:', error);
        return this.listFailure('Failed to fetch files', error);
    }
}
export function getFileRenderUrl(fileKey, size) {
    // Per OpenAPI: GET /files/render/{key}
    // Note: fileKey already contains the full path (e.g., "uploads/2025/11/18/file.jpg").
    // Web encodes the key (encodeRenderKeys); mobile passes it through verbatim.
    const key = this.options.encodeRenderKeys ? encodeURIComponent(fileKey) : fileKey;
    const sizeParam = size && size !== 'full' ? `?size=${size}` : '';
    return `${this.baseUrl}/files/render/${key}${sizeParam}`;
}
export async function addFile(workOrderId, file, metadata) {
    // Step 1: Upload file to /files/upload and get key
    const uploadResponse = await this.client.uploadFile('/files/upload', file);
    if (!uploadResponse.success || !uploadResponse.data?.key) {
        throw new Error(uploadResponse.error || 'File upload failed - no key returned');
    }
    const fileKey = uploadResponse.data.key;
    // Step 2: Attach file by key to work order or service
    const attachEndpoint = metadata?.service_id
        ? `/work-orders/${workOrderId}/services/${metadata.service_id}/files`
        : `/work-orders/${workOrderId}/files`;
    const attachPayload = {
        key: fileKey,
        label: metadata?.label || file.name || 'file',
    };
    // Add metadata if provided (only for work order files, not service files)
    if (metadata && !metadata.service_id) {
        if (metadata.service_name)
            attachPayload.service_name = metadata.service_name;
        if (metadata.comment)
            attachPayload.comment = metadata.comment;
        if (metadata.type)
            attachPayload.type = metadata.type;
    }
    else if (metadata?.comment) {
        // For service files, only comment is supported
        attachPayload.comment = metadata.comment;
    }
    const response = await this.client.post(attachEndpoint, attachPayload);
    if (!response.success) {
        throw new Error(response.error || 'Failed to attach file');
    }
}
/**
 * Upload file only (step 1 of two-step upload). Returns the server-assigned file key.
 * Used by the background upload queue for idempotent retries.
 *
 * Throws HttpError carrying the response status so the queue can decide
 * whether to retry (5xx/401/408/429) or bail immediately (other 4xx).
 */
export async function uploadFileOnly(file) {
    const uploadResponse = await this.client.uploadFile('/files/upload', file);
    if (!uploadResponse.success || !uploadResponse.data?.key) {
        throw new HttpError(uploadResponse.status ?? 0, uploadResponse.error || 'File upload failed - no key returned', uploadResponse.errorBody);
    }
    return uploadResponse.data.key;
}
/**
 * Attach an already-uploaded file key to a work order or service (step 2 of two-step upload).
 * Used by the background upload queue for idempotent retries.
 *
 * Throws HttpError carrying the response status so the queue can branch
 * on 404 (service deleted) / 409 (WO closed) and stop retrying instead
 * of burning MAX_RETRIES before pinning the task failed.
 */
export async function attachFile(workOrderId, fileKey, metadata) {
    const attachEndpoint = metadata?.service_id
        ? `/work-orders/${workOrderId}/services/${metadata.service_id}/files`
        : `/work-orders/${workOrderId}/files`;
    const attachPayload = {
        key: fileKey,
        label: metadata?.label || 'file',
    };
    if (metadata?.comment)
        attachPayload.comment = metadata.comment;
    if (metadata?.type && !metadata.service_id)
        attachPayload.type = metadata.type;
    const response = await this.client.post(attachEndpoint, attachPayload);
    if (!response.success) {
        throw new HttpError(response.status ?? 0, response.error || 'Failed to attach file', response.errorBody);
    }
}
export async function deleteFile(fileKey, workOrderId) {
    const encoded = encodeURIComponent(fileKey);
    // Try the work-order-scoped detach first (when a WO id is known), then
    // fall back to the storage-level delete. This also fixes the previous
    // bug where a missing workOrderId produced "/work-orders/undefined/...".
    const attempts = [];
    if (workOrderId)
        attempts.push(`/work-orders/${workOrderId}/files/${encoded}`);
    attempts.push(`/files/${encoded}`);
    const errors = [];
    for (const path of attempts) {
        try {
            const response = await this.client.delete(path);
            if (response.success)
                return;
            errors.push(`DELETE ${path} => ${response.error || 'failed'}`);
        }
        catch (error) {
            errors.push(`DELETE ${path} => ${error.message}`);
        }
    }
    console.error('[WorkOrdersService] Delete file error:', errors);
    throw new Error(`Failed to delete file: ${errors[0] || 'unknown error'}`);
}
export async function listServiceFiles(workOrderId, serviceId) {
    try {
        const response = await this.client.get(`/work-orders/${workOrderId}/services/${serviceId}/files`);
        if (!response.success || !response.data) {
            return this.listFailure(response.error || 'Failed to fetch service files');
        }
        const rawFiles = extractItems(response.data, ['files']);
        return rawFiles.map((f) => {
            const filename = f.file_name || (typeof f.key === 'string' ? f.key.split('/').pop() : '') || undefined;
            let comment = '';
            if (f.label) {
                const match = f.label.match(/^\[.*?\]\s*(.*)$/);
                comment = match ? match[1].trim() : '';
            }
            return {
                ...f,
                id: f.key,
                name: f.label,
                filename,
                comment: comment || undefined,
            };
        });
    }
    catch (error) {
        console.error('[WorkOrdersService] List service files error:', error);
        return this.listFailure('Failed to fetch service files', error);
    }
}
export async function deleteServiceFile(workOrderId, serviceId, fileKey) {
    try {
        const response = await this.client.delete(`/work-orders/${workOrderId}/services/${serviceId}/files/${encodeURIComponent(fileKey)}`);
        if (!response.success) {
            throw new Error(response.error || 'Failed to delete service file');
        }
    }
    catch (error) {
        console.error('[WorkOrdersService] Delete service file error:', error);
        throw error;
    }
}
